import { useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { speakText, stopSpeaking } from "@/lib/voice";
import { Logo } from "@/components/Logo";
import { Play } from "lucide-react";
import { toast } from "sonner";
import {
  Mic, Send, X, Loader2, ArrowRight, ExternalLink, ChevronDown, BookmarkPlus, Layers, Sparkles,
  Volume2, VolumeX, Compass, Settings2, Check,
} from "lucide-react";

const VOICES = [
  { id: "JBFqnCBsd6RMkjVDRZzb", label: "George — warm storyteller" },
  { id: "nPczCjzI2devNBz1zQrb", label: "Brian — deep & resonant" },
  { id: "pqHfZKP75CvOlQylNhV4", label: "Bill — wise & mature" },
  { id: "onwK4e9ZLuTAKqWW03F9", label: "Daniel — steady broadcaster" },
  { id: "pNInz6obpgDQGcFmaJgB", label: "Adam — firm & dominant" },
  { id: "IKne3meq5aSn9XLyUdCD", label: "Charlie — deep & confident" },
  { id: "cjVigY5qzO86Huf0OWal", label: "Eric — smooth & trustworthy" },
  { id: "CwhRBWXzGAHq8TQ4Fs17", label: "Roger — laid-back & resonant" },
  { id: "Xb7hH8MSUJpSbSDYk0k2", label: "Alice — engaging educator" },
  { id: "EXAVITQu4vr4xnSDxMaL", label: "Sarah — reassuring & confident" },
  { id: "XrExE9yKIg1WjnnlVkGX", label: "Matilda — knowledgeable & pro" },
];

// Free open-source (Piper) narrators used for reading entire texts and whenever the premium voice is resting.
const PIPER_VOICES = [
  { id: "ryan", label: "Ryan — deep American (cinematic)" },
  { id: "alan", label: "Alan — British storyteller" },
  { id: "amy", label: "Amy — warm American (female)" },
];
const SPEEDS = [
  { v: 0.85, label: "Slow" },
  { v: 1.0, label: "Normal" },
  { v: 1.12, label: "Brisk" },
];

const HOLD_MS = 260;
const DRAG_THRESH = 8;
const HERO_ROUTES = ["/", "/dashboard"];

export default function OracleWidget({ variant = "float" }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [listening, setListening] = useState(false);
  const [result, setResult] = useState(null);
  const [openLayer, setOpenLayer] = useState(0);
  const [saved, setSaved] = useState(false);
  const [voiceOn, setVoiceOn] = useState(() => localStorage.getItem("oracle_voice") !== "off");
  const [speaking, setSpeaking] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [voiceId, setVoiceId] = useState(() => localStorage.getItem("oracle_voice_id") || VOICES[0].id);
  const [piperVoice, setPiperVoice] = useState(() => localStorage.getItem("piper_voice") || "ryan");
  const [speed, setSpeed] = useState(() => parseFloat(localStorage.getItem("oracle_speed")) || 1.0);
  const [autoFly, setAutoFly] = useState(() => localStorage.getItem("oracle_autofly") === "on");
  const [pos, setPos] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem("oracle_pos"));
      if (s && typeof s.x === "number") {
        return {
          x: Math.max(8, Math.min(s.x, window.innerWidth - 72)),
          y: Math.max(8, Math.min(s.y, window.innerHeight - 72)),
        };
      }
    } catch {}
    return null;
  });
  const recRef = useRef(null);
  const cancelRef = useRef(false);
  const orbRef = useRef(null);
  const inputRef = useRef(null);
  const gesture = useRef({});

  // Floating orb is hidden on hero routes (the embedded hero takes over there).
  if (variant === "float" && (!user || HERO_ROUTES.includes(location.pathname))) return null;

  const stopAudio = () => { cancelRef.current = true; stopSpeaking(); setSpeaking(false); };

  const speak = async (text) => {
    if (!text || !user) return;
    if (speaking) { stopAudio(); return; }
    cancelRef.current = false;
    setSpeaking(true);
    await speakText(text, { voiceId, speed, isCanceled: () => cancelRef.current });
    if (!cancelRef.current) setSpeaking(false);
  };

  const toggleVoice = () => {
    setVoiceOn((v) => {
      const nv = !v;
      localStorage.setItem("oracle_voice", nv ? "on" : "off");
      if (!nv) stopAudio();
      return nv;
    });
  };
  const pickVoice = (id) => { setVoiceId(id); localStorage.setItem("oracle_voice_id", id); };
  const pickPiper = (id) => { setPiperVoice(id); localStorage.setItem("piper_voice", id); };
  const sampleVoice = (opts) => { stopSpeaking(); speakText("This is a sample of this voice reading your texts aloud.", opts); };
  const pickSpeed = (v) => { setSpeed(v); localStorage.setItem("oracle_speed", String(v)); };
  const toggleAutoFly = () => setAutoFly((v) => { const nv = !v; localStorage.setItem("oracle_autofly", nv ? "on" : "off"); return nv; });

  const traceOnMap = (to) => {
    if (!to) return;
    stopAudio();
    setOpen(false);
    navigate(`/map?focus=${encodeURIComponent(to)}`);
  };

  // ---- Speech recognition helpers (robust: no duplicate/repeated words, no overlap) ----
  const stopRecognition = () => {
    const r = recRef.current;
    if (r) {
      try { r.onresult = null; r.onend = null; r.onerror = null; r.abort(); } catch {}
      recRef.current = null;
    }
  };

  // ---- Voice: chat-box mic (toggle dictation into the textarea) ----
  const startVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error("Voice isn't supported in this browser — just type your question."); return; }
    if (listening) { stopRecognition(); setListening(false); return; }
    stopRecognition();
    const r = new SR();
    r.lang = "en-US"; r.interimResults = true; r.continuous = false; r.maxAlternatives = 1;
    r.onresult = (e) => {
      // Rebuild the whole transcript from the cumulative results each event (never accumulate → no duplicates).
      let full = "";
      for (let i = 0; i < e.results.length; i++) full += e.results[i][0].transcript + " ";
      setQuestion(full.replace(/\s+/g, " ").trim());
    };
    r.onend = () => { setListening(false); recRef.current = null; };
    r.onerror = () => { setListening(false); };
    recRef.current = r; setListening(true);
    try { r.start(); } catch { setListening(false); }
  };

  // ---- Push-to-talk: hold the orb, speak, release to auto-send ----
  const beginPTT = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error("Voice isn't supported in this browser — tap to type instead."); return; }
    stopRecognition();
    const r = new SR();
    r.lang = "en-US"; r.interimResults = true; r.continuous = true; r.maxAlternatives = 1;
    gesture.current.transcript = "";
    setOpen(true);
    r.onresult = (e) => {
      // Rebuild from the full cumulative results each event (never accumulate → no duplicates).
      let full = "";
      for (let i = 0; i < e.results.length; i++) full += e.results[i][0].transcript + " ";
      full = full.replace(/\s+/g, " ").trim();
      gesture.current.transcript = full;
      setQuestion(full);
    };
    r.onerror = () => { setListening(false); };
    r.onend = () => { setListening(false); };
    recRef.current = r; setListening(true);
    try { r.start(); } catch { setListening(false); }
  };

  const endPTT = () => {
    const t = (gesture.current.transcript || "").trim();
    stopRecognition();
    setListening(false);
    if (t) { setQuestion(t); setTimeout(() => ask(t), 150); }
    else toast("I didn't catch that — hold a moment longer and speak clearly.");
  };

  const ask = async (override) => {
    const q = (typeof override === "string" ? override : question).trim();
    if (!q || loading) return;
    if (listening) { stopRecognition(); setListening(false); }
    if (!user) { navigate("/register"); return; }
    setLoading(true); setResult(null); setSaved(false); setOpenLayer(0); setOpen(true);
    try {
      const { data } = await api.post("/oracle/ask", { question: q });
      setResult(data);
      if (voiceOn && data.answer) speak(data.answer);
      const gto = data?.guidance?.kind !== "external" ? data?.guidance?.to : null;
      if (autoFly && gto) setTimeout(() => traceOnMap(gto), 1100);
    } catch (e) {
      toast.error(e.response?.data?.detail || "The Oracle is resting. Try again shortly.");
    } finally { setLoading(false); }
  };

  // ---- Orb gesture: tap = open/type, hold = push-to-talk, drag (float) = move ----
  const clampPos = (x, y) => ({
    x: Math.max(8, Math.min(x, window.innerWidth - 72)),
    y: Math.max(8, Math.min(y, window.innerHeight - 72)),
  });

  const onPointerDown = (e) => {
    const g = gesture.current;
    g.id = e.pointerId; g.startX = e.clientX; g.startY = e.clientY;
    g.moved = false; g.dragging = false; g.talking = false;
    const rect = orbRef.current?.getBoundingClientRect();
    g.origX = rect?.left || 0; g.origY = rect?.top || 0;
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch {}
    g.holdTimer = setTimeout(() => {
      if (!g.moved) {
        if (!user && variant === "hero") { navigate("/register"); return; }
        g.talking = true; beginPTT();
      }
    }, HOLD_MS);
  };

  const onPointerMove = (e) => {
    const g = gesture.current;
    if (g.id !== e.pointerId) return;
    const dx = e.clientX - g.startX, dy = e.clientY - g.startY;
    if (!g.moved && Math.hypot(dx, dy) > DRAG_THRESH) {
      g.moved = true;
      clearTimeout(g.holdTimer);
      if (variant === "float" && !g.talking) g.dragging = true;
    }
    if (g.dragging) setPos(clampPos(g.origX + dx, g.origY + dy));
  };

  const onPointerUp = (e) => {
    const g = gesture.current;
    if (g.id !== e.pointerId) return;
    clearTimeout(g.holdTimer);
    if (g.talking) endPTT();
    else if (g.dragging) { try { localStorage.setItem("oracle_pos", JSON.stringify(pos)); } catch {} }
    else {
      // tap
      if (variant === "hero") {
        if (!user) navigate("/register");
        else { setOpen(true); setTimeout(() => inputRef.current?.focus(), 50); }
      } else {
        setOpen((v) => !v);
      }
    }
    g.id = null;
  };

  const goGuidance = (g) => {
    if (!g) return;
    if (g.kind === "external" && g.url) { window.open(g.url, "_blank", "noopener"); return; }
    if (g.to) { setOpen(false); navigate(g.to); }
  };
  const goStep = (s) => {
    if (s.url) { window.open(s.url, "_blank", "noopener"); return; }
    if (s.to) { setOpen(false); navigate(s.to); }
  };

  const savePath = async () => {
    if (!result?.course) return;
    try {
      await api.post("/oracle/paths", { question, course: result.course });
      setSaved(true);
      toast.success("Saved to My Paths", { action: { label: "View", onClick: () => { setOpen(false); navigate("/my-paths"); } } });
    } catch { toast.error("Could not save this path."); }
  };

  const g = result?.guidance;
  const course = result?.course;
  const waveKeyframes = `@keyframes oracleWave{0%,100%{transform:scaleY(0.28)}50%{transform:scaleY(1)}}`;

  const Toolbar = () => (
    <div className="flex items-center gap-2 mb-2">
      <button onClick={() => setShowSettings((s) => !s)} data-testid="oracle-settings-toggle" title="Voice & guidance settings"
        className={`${showSettings ? "text-primary" : "text-muted-foreground"} hover:opacity-80`}>
        <Settings2 className="w-5 h-5" />
      </button>
      <button onClick={toggleVoice} data-testid="oracle-voice-toggle" title={voiceOn ? "Voice on — Anubis reads answers aloud" : "Voice off"}
        className={`${voiceOn ? "text-primary" : "text-muted-foreground"} hover:opacity-80`}>
        {voiceOn ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
      </button>
      <span className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground">Voice {voiceOn ? "on" : "off"}</span>
    </div>
  );

  const Settings = () => (
    showSettings ? (
      <div className="mb-3 p-4 border border-border rounded-sm bg-muted/30 space-y-3 text-left" data-testid="oracle-settings">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground mb-1.5">Anubis's voice · premium</div>
          <div className="flex flex-col gap-1">
            {VOICES.map((v) => (
              <div key={v.id} className="flex items-center gap-1">
                <button onClick={() => pickVoice(v.id)} data-testid={`oracle-voice-${v.id}`}
                  className={`flex-1 flex items-center gap-2 text-left text-sm px-2 py-1.5 rounded-sm border transition-colors ${voiceId === v.id ? "border-primary text-primary bg-primary/10" : "border-transparent text-foreground/80 hover:bg-muted"}`}>
                  {voiceId === v.id ? <Check className="w-3.5 h-3.5 shrink-0" /> : <span className="w-3.5 h-3.5 shrink-0" />} {v.label}
                </button>
                <button onClick={() => sampleVoice({ voiceId: v.id, speed })} data-testid={`oracle-voice-sample-${v.id}`}
                  title="Hear a sample" className="shrink-0 w-7 h-7 flex items-center justify-center rounded-sm text-muted-foreground hover:text-primary hover:bg-muted">
                  <Play className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground mb-1.5">Free cinematic narrator · open-source</div>
          <p className="text-[11px] text-muted-foreground mb-1.5 leading-snug">Used to read entire texts and whenever the premium voice is resting.</p>
          <div className="flex flex-col gap-1">
            {PIPER_VOICES.map((v) => (
              <div key={v.id} className="flex items-center gap-1">
                <button onClick={() => pickPiper(v.id)} data-testid={`oracle-piper-${v.id}`}
                  className={`flex-1 flex items-center gap-2 text-left text-sm px-2 py-1.5 rounded-sm border transition-colors ${piperVoice === v.id ? "border-primary text-primary bg-primary/10" : "border-transparent text-foreground/80 hover:bg-muted"}`}>
                  {piperVoice === v.id ? <Check className="w-3.5 h-3.5 shrink-0" /> : <span className="w-3.5 h-3.5 shrink-0" />} {v.label}
                </button>
                <button onClick={() => sampleVoice({ piperVoice: v.id, speed })} data-testid={`oracle-piper-sample-${v.id}`}
                  title="Hear a sample" className="shrink-0 w-7 h-7 flex items-center justify-center rounded-sm text-muted-foreground hover:text-primary hover:bg-muted">
                  <Play className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground mb-1.5">Speed</div>
          <div className="flex gap-2">
            {SPEEDS.map((s) => (
              <button key={s.v} onClick={() => pickSpeed(s.v)} data-testid={`oracle-speed-${s.label.toLowerCase()}`}
                className={`flex-1 text-xs font-mono uppercase tracking-widest py-1.5 rounded-sm border transition-colors ${Math.abs(speed - s.v) < 0.01 ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:border-primary"}`}>
                {s.label}
              </button>
            ))}
          </div>
        </div>
        <button onClick={toggleAutoFly} data-testid="oracle-autofly-toggle"
          className="w-full flex items-center gap-2 text-sm px-2 py-2 rounded-sm border border-border hover:border-primary">
          <span className={`w-4 h-4 rounded-sm border flex items-center justify-center ${autoFly ? "bg-primary border-primary" : "border-muted-foreground"}`}>
            {autoFly && <Check className="w-3 h-3 text-primary-foreground" />}
          </span>
          <Compass className="w-3.5 h-3.5 text-primary" />
          <span className="text-left">Auto-fly the star map to each answer</span>
        </button>
      </div>
    ) : null
  );

  const Body = () => (
    <div className="text-left">
      <Toolbar />
      <Settings />
      <div className="relative">
        <textarea
          ref={inputRef}
          data-testid="oracle-input"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); } }}
          rows={2}
          placeholder="Speak or type — e.g. What is a cestui que trust?"
          className="w-full bg-muted border border-input rounded-sm pl-3 pr-20 py-2.5 text-sm outline-none focus:border-primary resize-none"
        />
        <div className="absolute right-2 top-2 flex items-center gap-1">
          <button onClick={startVoice} data-testid="oracle-mic" title="Speak your question"
            className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${listening ? "bg-primary text-primary-foreground animate-pulse" : "text-muted-foreground hover:text-primary"}`}>
            <Mic className="w-4 h-4" />
          </button>
          <button onClick={() => ask()} disabled={loading || !question.trim()} data-testid="oracle-ask"
            className="w-8 h-8 rounded-full flex items-center justify-center bg-primary text-primary-foreground disabled:opacity-40">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </div>
      {listening && <p className="text-[11px] text-primary font-mono mt-1.5">Listening… speak now</p>}

      {loading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground mt-5" data-testid="oracle-loading">
          <Loader2 className="w-4 h-4 animate-spin text-primary" /> The Oracle consults the archive…
        </div>
      )}

      {result && (
        <div className="mt-5 space-y-4" data-testid="oracle-result">
          <div className="flex items-start gap-2">
            <p className="text-sm text-foreground/90 leading-relaxed whitespace-pre-wrap flex-1" data-testid="oracle-answer">{result.answer}</p>
            <button onClick={() => speak(result.answer)} data-testid="oracle-speak"
              title={speaking ? "Stop" : "Read aloud"}
              className={`shrink-0 mt-0.5 ${speaking ? "text-primary" : "text-muted-foreground hover:text-primary"}`}>
              <Volume2 className="w-4 h-4" />
            </button>
          </div>
          {speaking && (
            <div className="flex items-end gap-0.5 h-4 -mt-2" data-testid="oracle-waveform">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <span key={i} className="w-1 bg-primary rounded-full" style={{ height: "100%", transformOrigin: "bottom", animation: "oracleWave 0.9s ease-in-out infinite", animationDelay: `${i * 0.11}s` }} />
              ))}
            </div>
          )}

          {g && (g.to || g.url) && (
            <div className="space-y-2">
              <button onClick={() => goGuidance(g)} data-testid="oracle-guidance"
                className="w-full flex items-center gap-2 bg-primary/10 border border-primary/40 hover:bg-primary/20 text-left px-4 py-3 rounded-sm transition-colors">
                {g.kind === "external" ? <ExternalLink className="w-4 h-4 text-primary shrink-0" /> : <ArrowRight className="w-4 h-4 text-primary shrink-0" />}
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-primary truncate">{g.label || "Go to source"}</span>
                  {g.reason && <span className="block text-xs text-muted-foreground truncate">{g.reason}</span>}
                </span>
              </button>
              {g.kind !== "external" && g.to && (
                <button onClick={() => traceOnMap(g.to)} data-testid="oracle-trace-map"
                  className="w-full flex items-center justify-center gap-2 text-xs font-mono uppercase tracking-widest text-primary/90 border border-dashed border-primary/40 hover:bg-primary/5 py-2 rounded-sm">
                  <Compass className="w-3.5 h-3.5" /> Trace this on the star map
                </button>
              )}
            </div>
          )}

          {Array.isArray(result.related) && result.related.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="oracle-related">
              {result.related.filter((r) => r.to).map((r, i) => (
                <button key={i} onClick={() => { setOpen(false); navigate(r.to); }}
                  className="text-xs font-mono px-2.5 py-1 rounded-full border border-border text-muted-foreground hover:border-primary hover:text-primary transition-colors">
                  {r.label}
                </button>
              ))}
            </div>
          )}

          {course && Array.isArray(course.layers) && course.layers.length > 0 && (
            <div className="border-t border-border pt-4" data-testid="oracle-course">
              <div className="flex items-center gap-2 mb-1">
                <Layers className="w-4 h-4 text-primary" />
                <h3 className="font-serif text-lg leading-tight">{course.title}</h3>
              </div>
              {course.summary && <p className="text-xs text-muted-foreground mb-3">{course.summary}</p>}
              <div className="space-y-2">
                {course.layers.map((layer, li) => (
                  <div key={li} className="border border-border rounded-sm overflow-hidden">
                    <button onClick={() => setOpenLayer(openLayer === li ? -1 : li)} data-testid={`oracle-layer-${li}`}
                      className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-muted">
                      <span className="w-5 h-5 rounded-full bg-primary/15 text-primary text-[10px] font-mono flex items-center justify-center shrink-0">{li + 1}</span>
                      <span className="text-sm font-medium flex-1 min-w-0">{layer.title}</span>
                      <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${openLayer === li ? "rotate-180" : ""}`} />
                    </button>
                    {openLayer === li && (
                      <div className="px-3 pb-3 pt-1 space-y-2">
                        {layer.summary && <p className="text-xs text-muted-foreground">{layer.summary}</p>}
                        {(layer.steps || []).map((s, si) => (
                          <div key={si} className="flex items-start gap-2 text-sm bg-muted/50 rounded-sm px-2.5 py-2">
                            <Sparkles className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
                            <div className="min-w-0 flex-1">
                              <div className="font-medium">{s.label}</div>
                              {s.detail && <div className="text-xs text-muted-foreground">{s.detail}</div>}
                              {(s.to || s.url) && (
                                <div className="flex items-center gap-3 mt-1">
                                  <button onClick={() => goStep(s)} className="text-[11px] font-mono uppercase tracking-widest text-primary hover:underline inline-flex items-center gap-1">
                                    Open {s.url ? <ExternalLink className="w-3 h-3" /> : <ArrowRight className="w-3 h-3" />}
                                  </button>
                                  {s.to && !s.url && (
                                    <button onClick={() => traceOnMap(s.to)} className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground hover:text-primary inline-flex items-center gap-1">
                                      <Compass className="w-3 h-3" /> Map
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <button onClick={savePath} disabled={saved} data-testid="oracle-save-path"
                className="mt-3 w-full flex items-center justify-center gap-2 border border-primary/40 text-primary text-xs font-mono uppercase tracking-widest py-2.5 rounded-sm hover:bg-primary/10 disabled:opacity-50">
                <BookmarkPlus className="w-4 h-4" /> {saved ? "Saved to My Paths" : "Save this path"}
              </button>
            </div>
          )}
        </div>
      )}
      <p className="mt-3 text-[10px] text-muted-foreground font-mono">Anubis is an educational AI guide — not legal advice.</p>
    </div>
  );

  // ---------------- HERO VARIANT (embedded on Landing & Dashboard) ----------------
  if (variant === "hero") {
    return (
      <div className="w-full flex flex-col items-center text-center" data-testid="oracle-hero">
        <style>{waveKeyframes}</style>
        <button
          ref={orbRef}
          data-testid="oracle-hero-orb"
          aria-label="Hold to speak to the Anubis Oracle, or tap to type"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onContextMenu={(e) => e.preventDefault()}
          className={`relative touch-none select-none w-40 h-40 sm:w-48 sm:h-48 rounded-full flex items-center justify-center border-2 bg-gradient-to-b from-primary/30 to-primary/10 backdrop-blur transition-transform active:scale-95 ${listening ? "border-primary shadow-[0_0_90px_rgba(212,160,60,0.85)]" : "border-primary/70 shadow-[0_0_60px_rgba(212,160,60,0.5)] hover:shadow-[0_0_80px_rgba(212,160,60,0.7)]"}`}
        >
          <Logo className="w-24 h-24 sm:w-28 sm:h-28" />
          <span className="absolute inset-0 rounded-full animate-ping border border-primary/30" />
          {(listening || speaking) && <span className="absolute -inset-2 rounded-full animate-pulse border-2 border-primary/50" />}
          {listening && (
            <span className="absolute -bottom-3 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-widest font-mono text-primary bg-background/90 px-2 py-0.5 rounded-full border border-primary/40 flex items-center gap-1">
              <Mic className="w-3 h-3" /> Listening…
            </span>
          )}
        </button>

        <h2 className="mt-8 font-serif text-3xl sm:text-4xl">Ask Anubis</h2>
        <p className="mt-2 text-sm text-muted-foreground max-w-md">
          {user
            ? "Hold the orb to speak your question — or tap to type. The Oracle answers and guides you to the right source."
            : "The Oracle of the Academy. Create a free account to ask Anubis anything by voice or text."}
        </p>

        {user && (open || result || loading) && (
          <div className="w-full max-w-xl mt-6 p-5 bg-card border border-border rounded-md shadow-2xl" data-testid="oracle-hero-panel">
            {Body()}
          </div>
        )}
        {!user && (
          <button onClick={() => navigate("/register")} data-testid="oracle-hero-cta"
            className="mt-6 inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3.5 rounded-sm text-sm hover:-translate-y-[1px] transition-transform">
            Begin Your Study <ArrowRight className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  }

  // ---------------- FLOAT VARIANT (draggable orb on all other pages) ----------------
  const orbStyle = pos ? { left: pos.x, top: pos.y } : { left: "1.5rem", bottom: "1.5rem" };
  const panelStyle = pos
    ? { left: Math.max(12, Math.min(pos.x, window.innerWidth - 436)), bottom: Math.max(88, window.innerHeight - pos.y + 12) }
    : { left: "1.5rem", bottom: "6rem" };

  return (
    <>
      <style>{waveKeyframes}</style>
      <button
        ref={orbRef}
        data-testid="oracle-orb"
        aria-label="Ask the Anubis Oracle — tap to open, hold to speak, drag to move"
        style={orbStyle}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onContextMenu={(e) => e.preventDefault()}
        className={`fixed z-[90] touch-none select-none w-16 h-16 rounded-full flex items-center justify-center border-2 bg-primary/25 backdrop-blur transition-shadow ${listening ? "border-primary shadow-[0_0_48px_rgba(212,160,60,0.9)]" : "border-primary/70 shadow-[0_0_28px_rgba(212,160,60,0.55)] hover:shadow-[0_0_40px_rgba(212,160,60,0.7)]"}`}
      >
        {open ? <X className="w-6 h-6 text-primary" /> : <Logo className="w-9 h-9" />}
        {!open && <span className="absolute inset-0 rounded-full animate-ping border border-primary/40" />}
        {listening && <span className="absolute -inset-1 rounded-full animate-pulse border-2 border-primary/60" />}
      </button>

      {open && (
        <div data-testid="oracle-panel" style={panelStyle}
          className="fixed z-[90] w-[min(420px,calc(100vw-3rem))] max-h-[75vh] flex flex-col bg-card border border-border rounded-md shadow-2xl overflow-hidden">
          <div className="flex items-center gap-3 px-5 py-4 border-b border-border bg-gradient-to-r from-primary/10 to-transparent">
            <Logo className="w-7 h-7" />
            <div>
              <div className="font-serif text-lg leading-none">Anubis</div>
              <div className="text-[10px] uppercase tracking-widest font-mono text-primary mt-0.5">Oracle of the Academy</div>
            </div>
            <button onClick={() => setOpen(false)} className="ml-auto text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
          </div>
          <div className="p-5 overflow-y-auto">{Body()}</div>
        </div>
      )}
    </>
  );
}
